import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Stack,
  Autocomplete,
} from '@mui/material';
import { Edit, Settings, PersonOff, PersonAddAlt1 } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROLES, VISIBLE_ROLES, ROLE_COLORS, ROLE_DISPLAY_NAMES, isFlightCrewRole } from '../constants/roles';
import { roleMatches } from '../utils/rolePermissions';
import {
  emptyCrewClearanceFormValues,
  crewClearanceFromUser,
  appendCrewClearancePayload,
  CREW_CLEARANCE_CONFIG
} from '../utils/crewClearance';
import CrewClearanceFields from './onboarding/CrewClearanceFields';
import CrewClearanceChips from './ops/CrewDutyRoster/components/CrewClearanceChips';
import CertificateManagement from './onboarding/CertificateManagement';
import TrainingManagement from './onboarding/TrainingManagement';
import ProfileSignature from './onboarding/ProfileSignature';

const GENDER_LABELS = { MALE: 'Male', FEMALE: 'Female', OTHER: 'Other' };

const emptyWorkLocation = () => ({
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateRegion: '',
  postalCode: '',
  country: ''
});

const formatLocationShort = (wl) => {
  if (!wl) return '—';
  const parts = [wl.city, wl.stateRegion, wl.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: ROLES.CREW,
    gender: '',
    workLocation: emptyWorkLocation(),
    ...emptyCrewClearanceFormValues(),
    department: '',
    phone: ''
  });
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageUser, setManageUser] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [deactivateBusy, setDeactivateBusy] = useState(false);
  const [aircraftList, setAircraftList] = useState([]);
  const [operatorList, setOperatorList] = useState([]);
  const { user, canWrite } = useAuth();
  const navigate = useNavigate();

  const roles = VISIBLE_ROLES;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchAircraft = async () => {
      try {
        const response = await axios.get('/api/aircraft');
        setAircraftList(response.data || []);
      } catch (error) {
        console.error('Error fetching aircraft:', error);
      }
    };
    const fetchOperators = async () => {
      try {
        const response = await axios.get('/api/master-data/operators', { params: { isActive: 'true' } });
        setOperatorList(response.data || []);
      } catch (error) {
        console.error('Error fetching operators:', error);
      }
    };
    if (roleMatches(user?.role, [ROLES.SUPER_ADMIN])) {
      fetchAircraft();
      fetchOperators();
    }
  }, [user?.role]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (selectedUser) {
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          department: formData.department,
          gender: formData.gender || undefined,
          workLocation: formData.workLocation
        };
        if (canManageUsers) {
          updateData.role = formData.role;
          if (formData.password?.trim()) {
            updateData.password = formData.password;
          }
          if (formData.role === ROLES.CLIENT) {
            updateData.assignedAircraftIds = (formData.assignedAircraftIds || []).map((a) => a._id || a);
          }
          if (formData.role === ROLES.EXTERNAL_OPERATOR) {
            updateData.operatorId = formData.operatorId?._id || formData.operatorId || null;
          }
        }
        appendCrewClearancePayload(
          updateData,
          canManageUsers ? formData.role : ROLES.CREW,
          formData
        );
        await axios.put(`/api/users/${selectedUser._id}`, updateData);
        fetchUsers();
        handleClose();
      } else {
        // New users should be created through onboarding workflow
        navigate('/users/onboard/new');
      }
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
    setIsEditMode(false);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: ROLES.CREW,
      gender: '',
      workLocation: emptyWorkLocation(),
      ...emptyCrewClearanceFormValues(),
      department: '',
      phone: ''
    });
  };

  const handleEdit = (userItem) => {
    setSelectedUser(userItem);
    const wl = userItem.workLocation || {};
    setFormData({
      ...userItem,
      password: '',
      gender: userItem.gender || '',
      assignedAircraftIds: getAssignedAircraftForForm(userItem),
      operatorId: getOperatorForForm(userItem),
      workLocation: {
        addressLine1: wl.addressLine1 || '',
        addressLine2: wl.addressLine2 || '',
        city: wl.city || '',
        stateRegion: wl.stateRegion || '',
        postalCode: wl.postalCode || '',
        country: wl.country || ''
      },
      ...crewClearanceFromUser(userItem)
    });
    setIsEditMode(true);
    setOpen(true);
  };

  const getAssignedAircraftForForm = (userItem) => {
    if (!userItem?.assignedAircraftIds?.length) return [];
    return userItem.assignedAircraftIds.map((item) => {
      if (typeof item === 'object' && item._id) {
        const fromList = aircraftList.find((a) => a._id === item._id);
        return fromList || item;
      }
      return aircraftList.find((a) => a._id === item) || { _id: item, registration: item };
    });
  };

  const getOperatorForForm = (userItem) => {
    if (!userItem?.operatorId) return null;
    if (typeof userItem.operatorId === 'object' && userItem.operatorId._id) {
      return operatorList.find((o) => o._id === userItem.operatorId._id) || userItem.operatorId;
    }
    return operatorList.find((o) => o._id === userItem.operatorId) || { _id: userItem.operatorId };
  };

  const handleManageCompliance = (userItem) => {
    setManageUser(userItem);
    setManageDialogOpen(true);
  };

  const openDeactivateDialog = (userItem) => {
    setDeactivateTarget(userItem);
    setDeactivationReason('');
    setDeactivateDialogOpen(true);
  };

  const closeDeactivateDialog = () => {
    setDeactivateDialogOpen(false);
    setDeactivateTarget(null);
    setDeactivationReason('');
    setDeactivateBusy(false);
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      setDeactivateBusy(true);
      await axios.post(`/api/users/${deactivateTarget._id}/deactivate`, {
        reason: deactivationReason.trim() || undefined
      });
      closeDeactivateDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      alert(error.response?.data?.message || 'Failed to deactivate user.');
      setDeactivateBusy(false);
    }
  };

  const handleReactivate = async (userItem) => {
    if (!window.confirm(
      `Reactivate ${userItem.firstName} ${userItem.lastName}? They will be able to sign in again. Past cancelled duties and charter assignments are not restored automatically.`
    )) {
      return;
    }
    try {
      await axios.post(`/api/users/${userItem._id}/reactivate`);
      fetchUsers();
    } catch (error) {
      console.error('Error reactivating user:', error);
      alert(error.response?.data?.message || 'Failed to reactivate user.');
    }
  };

  const getRoleColor = (role) => {
    return ROLE_COLORS[role] || 'default';
  };

  const canManageUsers = canWrite && roleMatches(user?.role, [ROLES.SUPER_ADMIN]);
  const isOpsManager = user?.role === ROLES.OPERATIONS_MANAGER;
  const canEditCrewDetails = canWrite && (canManageUsers || isOpsManager);
  const canManageCrewCertificates = (crewUser) =>
    canWrite && (
      user?.role === ROLES.SUPER_ADMIN ||
      (isOpsManager && isFlightCrewRole(crewUser?.role))
    );
  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return users;

    return users.filter((userItem) => {
      const fullName = `${userItem.firstName || ''} ${userItem.lastName || ''}`.trim();
      return (
        fullName.toLowerCase().includes(query) ||
        (userItem.email || '').toLowerCase().includes(query) ||
        (userItem.department || '').toLowerCase().includes(query) ||
        (userItem.phone || '').toLowerCase().includes(query)
      );
    });
  }, [users, searchTerm]);
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">
            {isOpsManager ? 'Crew Management' : 'User Management'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isOpsManager
              ? 'Edit crew details and manage certificates for pilots.'
              : 'Manage existing users. To add new users, use the User Onboarding workflow.'}
          </Typography>
        </Box>
        {canManageUsers && (
          <Button
            variant="outlined"
            onClick={() => navigate('/users/onboard/new')}
          >
            New User Onboarding
          </Button>
        )}
      </Box>

      <Box mb={2}>
        <TextField
          fullWidth
          size="small"
          label="Search users"
          placeholder="Search by name, email, department, or phone"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>


      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Base location</TableCell>
              <TableCell>Clearances (Crew)</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Onboarding</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Assigned Aircraft</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {filteredUsers.map((userItem) => (
              <TableRow key={userItem._id}>
                <TableCell>
                  {userItem.firstName} {userItem.lastName}
                </TableCell>
                <TableCell>{userItem.email}</TableCell>
                <TableCell>
                  <Chip
                    label={
                      userItem.role
                        ? (ROLE_DISPLAY_NAMES[userItem.role] || userItem.role.replace(/_/g, ' '))
                        : 'Unknown'
                    }
                    color={getRoleColor(userItem.role)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{userItem.gender ? (GENDER_LABELS[userItem.gender] || userItem.gender) : '—'}</TableCell>
                <TableCell>{formatLocationShort(userItem.workLocation)}</TableCell>
                <TableCell>
                  {isFlightCrewRole(userItem.role) &&
                  CREW_CLEARANCE_CONFIG.some(({ field }) => userItem[field] === true) ? (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      <CrewClearanceChips crew={userItem} />
                    </Stack>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{userItem.department || '-'}</TableCell>
                <TableCell>{userItem.phone || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={userItem.isActive ? 'Active' : 'Deactivated'}
                    color={userItem.isActive ? 'success' : 'default'}
                    size="small"
                    title={userItem.deactivationReason || undefined}
                  />
                </TableCell>
                <TableCell>
                  {userItem.onboardingStatus ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={userItem.onboardingStatus.replace(/_/g, ' ')}
                        size="small"
                        color="primary"
                      />
                      {userItem.onboardingId && (
                        <Button
                          size="small"
                          onClick={() => navigate(`/users/onboard/${userItem.onboardingId}`)}
                        >
                          View
                        </Button>
                      )}
                    </Stack>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  {userItem.lastLogin ? new Date(userItem.lastLogin).toLocaleDateString() : 'Never'}
                </TableCell>
                <TableCell>
                  {userItem.role === ROLES.CLIENT && userItem.assignedAircraftIds?.length
                    ? userItem.assignedAircraftIds
                        .map((a) => (typeof a === 'object' ? a.registration : a))
                        .join(', ')
                    : userItem.role === ROLES.EXTERNAL_OPERATOR
                      ? (typeof userItem.operatorId === 'object'
                        ? (userItem.operatorId?.code || userItem.operatorId?.name || '—')
                        : (userItem.operatorId || '—'))
                      : userItem.role === ROLES.CLIENT ? '—' : 'N/A'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {userItem.isActive && (canManageUsers || (isOpsManager && isFlightCrewRole(userItem.role))) && (
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleEdit(userItem)}
                      >
                        Edit
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleManageCompliance(userItem)}
                      title="Manage compliance"
                    >
                      <Settings fontSize="small" />
                    </IconButton>
                    {canManageUsers && userItem._id !== user?._id && (
                      userItem.isActive ? (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<PersonOff />}
                          onClick={() => openDeactivateDialog(userItem)}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="success"
                          startIcon={<PersonAddAlt1 />}
                          onClick={() => handleReactivate(userItem)}
                        >
                          Reactivate
                        </Button>
                      )
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={15} align="center">
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open && isEditMode} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit User
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name (optional)"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!canEditCrewDetails}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={selectedUser ? "New Password (leave blank to keep current)" : "Password"}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!selectedUser}
                disabled={!canManageUsers}
                helperText={!canManageUsers ? 'Password changes are Super Admin only' : undefined}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Role"
                value={formData.role}
                disabled={!canManageUsers}
                onChange={(e) => {
                  const role = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    role,
                    ...(isFlightCrewRole(role) ? {} : emptyCrewClearanceFormValues()),
                    ...(role === ROLES.CLIENT ? {} : { assignedAircraftIds: [] }),
                    ...(role === ROLES.EXTERNAL_OPERATOR ? {} : { operatorId: null })
                  }));
                }}
              >
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {ROLE_DISPLAY_NAMES[role] || (role ? role.replace(/_/g, ' ') : 'Unknown')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Gender"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <MenuItem value="">Not set</MenuItem>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
            {isFlightCrewRole(formData.role) && (
              <CrewClearanceFields
                formData={formData}
                onChange={(field) => (e) => setFormData({ ...formData, [field]: e.target.value })}
              />
            )}
            {formData.role === ROLES.CLIENT && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={aircraftList}
                  getOptionLabel={(option) =>
                    `${option.registration} — ${option.manufacturer} ${option.model}`
                  }
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  value={formData.assignedAircraftIds || []}
                  onChange={(_, newValue) =>
                    setFormData({ ...formData, assignedAircraftIds: newValue })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assigned Aircraft"
                      placeholder="Select aircraft for this client user"
                    />
                  )}
                />
              </Grid>
            )}
            {formData.role === ROLES.EXTERNAL_OPERATOR && (
              <Grid item xs={12}>
                <Autocomplete
                  options={operatorList}
                  getOptionLabel={(option) =>
                    option?.code ? `${option.code} — ${option.name}` : (option?.name || '')
                  }
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  value={formData.operatorId || null}
                  onChange={(_, newValue) =>
                    setFormData({ ...formData, operatorId: newValue })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Operator"
                      placeholder="Select Operator for this portal user"
                      required
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Base work / reporting location</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address line 1"
                value={formData.workLocation.addressLine1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    workLocation: { ...formData.workLocation, addressLine1: e.target.value }
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address line 2"
                value={formData.workLocation.addressLine2}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    workLocation: { ...formData.workLocation, addressLine2: e.target.value }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.workLocation.city}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    workLocation: { ...formData.workLocation, city: e.target.value }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State / region"
                value={formData.workLocation.stateRegion}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    workLocation: { ...formData.workLocation, stateRegion: e.target.value }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Postal code"
                value={formData.workLocation.postalCode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    workLocation: { ...formData.workLocation, postalCode: e.target.value }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.workLocation.country}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    workLocation: { ...formData.workLocation, country: e.target.value }
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Manage Compliance</DialogTitle>
        <DialogContent dividers>
          {manageUser ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {manageUser.firstName} {manageUser.lastName} ({manageUser.email})
              </Typography>
              {!manageUser.isActive && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This user is deactivated. History and compliance records are retained for reference.
                </Typography>
              )}
              <ProfileSignature
                userId={manageUser._id}
                signatureImageUrl={manageUser.signatureImageUrl}
                readOnly={!canWrite || user?.role !== ROLES.SUPER_ADMIN || !manageUser.isActive}
                onUpdated={async () => {
                  try {
                    const response = await axios.get('/api/users');
                    setUsers(response.data);
                    const refreshed = (response.data || []).find((u) => u._id === manageUser._id);
                    if (refreshed) setManageUser(refreshed);
                  } catch (error) {
                    console.error('Error refreshing users after signature update:', error);
                  }
                }}
              />
              <Box mt={3}>
                <CertificateManagement
                  entityId={manageUser._id}
                  mode="user"
                  readOnly={!canManageCrewCertificates(manageUser) || !manageUser.isActive}
                  userRole={user?.role}
                />
                <TrainingManagement
                  entityId={manageUser._id}
                  mode="user"
                  readOnly={!canWrite || ![ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role) || !manageUser.isActive}
                  userRole={user?.role}
                />
              </Box>
            </Box>
          ) : (
            <Typography>No user selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deactivateDialogOpen} onClose={closeDeactivateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          {deactivateTarget && (
            <Stack spacing={2} mt={1}>
              <Typography>
                Deactivate <strong>{deactivateTarget.firstName} {deactivateTarget.lastName}</strong>
                {' '}({deactivateTarget.email})?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                They will not be able to sign in or take any actions. All history stays in the system.
                {isFlightCrewRole(deactivateTarget.role)
                  ? ' Future and in-progress duty roster entries will be cancelled, and they will be removed from open charter assignments. Completed history is kept.'
                  : ''}
              </Typography>
              <TextField
                label="Reason (optional)"
                value={deactivationReason}
                onChange={(e) => setDeactivationReason(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivateDialog} disabled={deactivateBusy}>Cancel</Button>
          <Button
            onClick={handleDeactivate}
            color="warning"
            variant="contained"
            disabled={deactivateBusy}
          >
            {deactivateBusy ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;